import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const CANCEL_REASONS = [
  "Driver is taking too long",
  "I no longer need a ride",
  "Booked by mistake",
  "Driver isn't moving toward me",
  "Found another way to get there",
  "Safety concern",
];

/**
 * CancelRideModal — passenger cancellation with reason capture.
 *
 * Backend rules: cancellations within 5 min of driver acceptance flag a
 * JMD 200 fee, but admin reviews the reason before charging. So we
 * surface this clearly: the user picks (or types) a reason and we
 * confirm whether a fee may apply BEFORE submitting.
 */
export default function CancelRideModal({ ride, open, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!ride) return null;

  // Show the late-fee notice if the ride is already accepted by a driver.
  // The backend re-checks the timestamp; we just preview the rule here.
  const showFeeNotice = !!ride.accepted_at;

  const submit = async () => {
    const finalReason = (reason === "Other" ? custom : reason).trim();
    if (!finalReason) { toast.error("Please pick or type a reason"); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/chauffeur/rides/${ride.id}/cancel`, { reason: finalReason });
      toast.success(res.data?.message || "Ride cancelled");
      onCancelled && onCancelled(res.data);
      onClose && onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Could not cancel"));
    } finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="cancel-ride-modal"
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
            className="bg-zinc-950 border border-rose-500/40 rounded-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-rose-500/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-400" />
                <div className="text-rose-200 font-display text-lg">Cancel ride?</div>
              </div>
              <button data-testid="cancel-modal-close" onClick={onClose} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {showFeeNotice && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-100/85 leading-relaxed">
                  This ride was already accepted by a driver. If you cancel within 5 minutes of acceptance,
                  a <strong>JMD 200</strong> fee may apply — but only after admin reviews your reason.
                  No charge if the cancellation was justified (driver no-show, safety concern, etc.).
                </div>
              )}

              <div>
                <label className="text-xs text-white/55 uppercase tracking-wider mb-2 block">Reason</label>
                <div className="space-y-1.5">
                  {CANCEL_REASONS.map((r) => (
                    <label key={r} className="flex items-center gap-2 text-sm text-white/85 cursor-pointer">
                      <input
                        type="radio" name="cancel-reason" value={r}
                        checked={reason === r}
                        onChange={() => setReason(r)}
                        className="accent-rose-500"
                      /> {r}
                    </label>
                  ))}
                  <label className="flex items-center gap-2 text-sm text-white/85 cursor-pointer">
                    <input
                      type="radio" name="cancel-reason" value="Other"
                      checked={reason === "Other"}
                      onChange={() => setReason("Other")}
                      className="accent-rose-500"
                    /> Other
                  </label>
                  {reason === "Other" && (
                    <textarea
                      data-testid="cancel-custom-reason"
                      value={custom} onChange={(e) => setCustom(e.target.value)}
                      placeholder="Tell us briefly what happened…"
                      rows={3}
                      className="mt-2 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    />
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  data-testid="cancel-keep-ride"
                  onClick={onClose}
                  className="flex-1 text-sm text-white/70 hover:text-white border border-white/15 rounded-md py-2.5 transition-colors"
                >
                  Keep ride
                </button>
                <button
                  data-testid="cancel-confirm"
                  onClick={submit}
                  disabled={submitting || !reason || (reason === "Other" && !custom.trim())}
                  className="flex-1 text-sm font-semibold text-white bg-rose-500 hover:bg-rose-400 rounded-md py-2.5 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Cancel ride
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
